import React, { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useTranslation } from '../../../shared/i18n';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Text } = Typography;
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const ok = (r) => r && (r.status === 'success' || r.success === true);

export const RDNS_MODE_COLORS = { zone: 'green', manual: 'blue', none: 'default' };

/** Per-pool reverse DNS: none / zone (reverse zone delegated to our DNS) / manual (upstream panel). */
export const RdnsPoolModal = ({ pool, onClose }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [zones, setZones] = useState([]);
    const [saving, setSaving] = useState(false);
    const mode = Form.useWatch('rdns_mode', form);

    useEffect(() => {
        if (!pool) return;
        form.setFieldsValue({ rdns_mode: pool.rdns_mode || 'none', rdns_zone_id: pool.rdns_zone_id || undefined, rdns_upstream: pool.rdns_upstream || '' });
        evmAdminService.getRdnsZones().then((r) => ok(r) && setZones(r.data?.items || []));
    }, [pool, form]);

    const save = async (v) => {
        setSaving(true);
        const r = await evmAdminService.updateIpPool(pool.id, { rdns_mode: v.rdns_mode, rdns_zone_id: v.rdns_zone_id || 0, rdns_upstream: v.rdns_upstream || '' });
        setSaving(false);
        if (ok(r)) { message.success(t('Saved')); onClose(true); } else message.error(r?.msg || t('Save failed'));
    };

    return (
        <Modal title={`rDNS · ${pool?.name || ''}`} open={!!pool} onCancel={() => onClose(false)} onOk={() => form.submit()} confirmLoading={saving} destroyOnClose>
            <Form form={form} layout="vertical" onFinish={save}>
                <Form.Item name="rdns_mode" label={t('Method')}>
                    <Select options={[
                        { value: 'none', label: t('Not offered') },
                        { value: 'zone', label: t('Our DNS (reverse zone delegated to us)') },
                        { value: 'manual', label: t('Upstream panel (ops sets it, confirmed automatically)') },
                    ]} />
                </Form.Item>
                {mode === 'zone' && (
                    <Form.Item name="rdns_zone_id" label={t('Reverse zone')} rules={[{ required: true }]}
                        extra={t('Create it in DNS first, e.g. 0-25.63.251.162.in-addr.arpa')}>
                        <Select showSearch optionFilterProp="label" options={zones.map((z) => ({ value: z.id, label: z.zone }))} />
                    </Form.Item>
                )}
                {mode === 'manual' && (
                    <Form.Item name="rdns_upstream" label={t('Upstream')} extra={t('Shown in the ops notification')}>
                        <Input placeholder="GTHost (cp.gthost.com)" />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

/** All PTR requests; pending manual ones first. */
export const RdnsRequestsDrawer = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('pending');
    const [page, setPage] = useState(1);
    const [rejecting, setRejecting] = useState(null);
    const [reason, setReason] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const r = await evmAdminService.getRdnsRequests({ status, page, page_size: 20 });
        setLoading(false);
        if (ok(r)) { setRows(r.data?.items || []); setTotal(r.data?.total || 0); }
    }, [status, page]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const done = async (id) => {
        const r = await evmAdminService.rdnsDone(id);
        if (ok(r)) load(); else message.error(r?.msg || t('Failed'));
    };
    const reject = async () => {
        const r = await evmAdminService.rdnsReject(rejecting.id, reason);
        if (ok(r)) { setRejecting(null); setReason(''); load(); } else message.error(r?.msg || t('Failed'));
    };

    const columns = [
        { title: t('Address'), dataIndex: 'address', render: (v) => <Text copyable style={mono}>{v}</Text> },
        { title: 'PTR', dataIndex: 'ptr', render: (v) => (v ? <Text copyable style={mono}>{v}</Text> : <Tag>{t('Restore default')}</Tag>) },
        { title: t('Status'), dataIndex: 'status', render: (v, r) => <Tag color={{ active: 'success', pending: 'processing', failed: 'error' }[v]} title={r.error || ''}>{t(v === 'active' ? 'Active' : v === 'pending' ? 'Pending' : 'Failed')}</Tag> },
        { title: t('IP pool'), dataIndex: 'pool_name', render: (v, r) => <Space size={4}>{v}<Tag color={RDNS_MODE_COLORS[r.mode]}>{r.mode}</Tag></Space> },
        { title: 'VM', dataIndex: 'vm_id', render: (v, r) => <Text>#{v} · uid {r.uid}</Text> },
        { title: t('Requested'), dataIndex: 'requested_at' },
        {
            title: '',
            key: 'act',
            render: (_, r) => r.status === 'pending' && (
                <Space size={4}>
                    <Popconfirm title={t('Set in the upstream panel?')} onConfirm={() => done(r.id)}>
                        <Button size="small" type="primary">{t('Done')}</Button>
                    </Popconfirm>
                    <Button size="small" danger onClick={() => setRejecting(r)}>{t('Reject')}</Button>
                </Space>
            ),
        },
    ];

    return (
        <Drawer title={t('rDNS requests')} open={open} onClose={onClose} width={980}
            extra={<Select value={status} style={{ width: 120 }} onChange={(v) => { setStatus(v); setPage(1); }}
                options={[{ value: 'pending', label: t('Pending') }, { value: 'active', label: t('Active') }, { value: 'failed', label: t('Failed') }, { value: '', label: t('All') }]} />}>
            <Table size="small" rowKey="id" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 'max-content' }}
                pagination={{ current: page, total, pageSize: 20, onChange: setPage }} />
            <Modal title={t('Reject')} open={!!rejecting} onCancel={() => setRejecting(null)} onOk={reject} okButtonProps={{ danger: true, disabled: !reason.trim() }}>
                <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('Reason (shown to the customer)')} />
            </Modal>
        </Drawer>
    );
};
